import React, { useCallback, useEffect, useRef, useState } from 'react';
import Form from 'devextreme-react/form';
import 'devextreme-react/autocomplete';
import 'devextreme-react/date-range-box';
import { IUpdateProfile } from '@/core/interfaces/usestate-interfaces';
import { ELEMENT_ID } from '@/core/constants/common/element-constants';
import FormWrapper from '@/core/wrappers/form-wrapper';
import { BUTTON_CONSTANTS } from '@/core/constants/common/button-contants';
import i18next, { t } from 'i18next';
import { profileService } from '@/core/services/profile-service';
import { UserManagement } from '@/core/user/user-management';
import { useAuth } from '@/modules/auth/core/Auth';
import CustomStore from 'devextreme/data/custom_store';
import { cultureData } from '@/core/datas/culture-data'
import { languageService } from '@/core/services/language-service';
import { LocalStorageHelper } from '@/core/helper/local-storage-helper';

function ProfileInformations() {
    const formRef: any = useRef<Form>();
    const [cultures, setCultures] = useState({});
    const [languages, setLanguages] = useState<CustomStore>();
    const { refreshAuth } = useAuth();
    const [user] = useState<IUpdateProfile>({
        email: UserManagement.getEmail(),
        firstName: UserManagement.getName(),
        lastName: UserManagement.getFamilyName(),
        languageId: UserManagement.getLanguageId(),
        culture: UserManagement.getCulture()
    });

    const handleSubmit = useCallback(async (): Promise<void> => {
        return new Promise(async (resolve, reject) => {
            try {
                const userForm = formRef.current.props.formData;
                await profileService.updateProfileAsync(userForm)
                await refreshAuth();
                await i18next.changeLanguage(LocalStorageHelper.getLanguage());
                resolve();
            } catch (error) {
                reject(error)
            }
        })
    }, []);

    useEffect(() => {

        setLanguages(new CustomStore({
            key: 'id',
            load: languageService.getLanguages,
            byKey: async (key, loadOptions: any) => {
                let returnObject = null;
                if (key) {
                    loadOptions.filter = ["Id", "=", key];
                    const objectsByID = await languageService.getLanguages(loadOptions)
                    returnObject = objectsByID.data.length > 0 ? objectsByID.data[0] : null;
                }
                return returnObject
            },
            useDefaultSearch: true,
        }));

        const fetchCultures = async () => {
            setCultures(cultureData);
        };

        fetchCultures();

    }, [user])

    const firstNameEditorOptions = {
        inputAttr: { [ELEMENT_ID]: 'firstName' },
    };

    const lastNameEditorOptions = {
        inputAttr: { [ELEMENT_ID]: 'lastName' },
    };

    const emailEditorOptions = {
        inputAttr: { [ELEMENT_ID]: 'email' },
    };



    const languageEditorOptions = {
        inputAttr: { [ELEMENT_ID]: 'language' },
        dataSource: languages,
        displayExpr: 'description',
        valueExpr: 'id',
        placeholder: undefined,
    };

    const cultureEditorOptions = {
        inputAttr: { [ELEMENT_ID]: 'culture' },
        dataSource: cultures,
        displayExpr: 'culture',
        valueExpr: 'culture',
        placeholder: undefined,
    };

    const noDigitsPattern = /^[^0-9]+$/;

    return (
        <FormWrapper
            onSubmit={handleSubmit}
            formRef={formRef}
            formData={user}
            isAddBackBtn={false}
            buttonGroups={[
                { buttonOptions: BUTTON_CONSTANTS.SaveBtn }
            ]}
            simpleItemGroups={[
                {
                    caption: t('profile_informations'),
                    useColCountByScreen: true,
                    items: [
                        {
                            dataField: "firstName",
                            editorOptions: firstNameEditorOptions,
                            label: 'firstname',
                            isRequired: true,
                            requiredMessage: 'firstname',
                            pattern: noDigitsPattern,
                            patternMessage: 'Do not use digits in the Firstname'
                        },
                        {
                            dataField: "lastName",
                            editorOptions: lastNameEditorOptions,
                            label: 'lastname',
                            isRequired: true,
                            requiredMessage: 'lastname',
                            pattern: noDigitsPattern,
                            patternMessage: 'Do not use digits in the Lastname'
                        },
                        {
                            dataField: "email",
                            editorType: "dxTextBox",
                            editorOptions: emailEditorOptions,
                            label: 'email',
                            isRequired: true,
                            requiredMessage: 'email',
                            email: true,
                            emailMessage: 'Email is invalid',
                        },
                        {
                            dataField: "languageId",
                            editorType: "dxSelectBox",
                            editorOptions: languageEditorOptions,
                            label: 'language',
                            isRequired: true,
                            requiredMessage: 'language',
                        },
                        {
                            dataField: "culture",
                            editorType: "dxSelectBox",
                            editorOptions: cultureEditorOptions,
                            label: 'culture',
                            isRequired: true,
                            requiredMessage: 'culture',
                        },
                    ]
                }
            ]}
        />
    )
}

export default ProfileInformations;
